/**
 * React Query hooks for a plugin's content-pack bill (ADR-021 §11).
 *
 * Endpoints:
 *   GET /contributor/arches/:archeId/content-billing
 *   PUT /contributor/arches/:archeId/content-billing/alert-threshold
 *   PUT /contributor/arches/:archeId/content-billing/hard-cap
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useContentPackBilling(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.contentBilling(archeId),
    queryFn: () => api.getContentPackBilling(archeId),
    enabled: !!archeId,
    // A 403 is an answer ("not your bill"), not a failure worth retrying.
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });
}

export function useSetContentPackAlertThreshold(archeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (thresholdBytes: number | null) =>
      api.setContentPackAlertThreshold(archeId, thresholdBytes),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.contributorArches.contentBilling(archeId), data);
    },
  });
}

export function useSetContentPackHardCap(archeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (capBytes: number | null) => api.setContentPackHardCap(archeId, capBytes),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.contributorArches.contentBilling(archeId), data);
    },
  });
}
